import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Filter, Download, FileText } from "lucide-react";
import moment from "moment/moment";
import ReactAudioPlayer from "react-audio-player";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import axios from "axios";
import toast from "react-hot-toast";
import LinkifyText from "./LinkifyText";

import { BACKEND_API_URL } from "../utils/api";
const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const Comments = ({ comments, filterComponent, filterEnabled, tableName, onModification }) => {

    const { user } = useSelector((state) => state.auth);
    const username = user.username;


    // Helper function to get full media URL
    // In production, use hardcoded URL for media files served from onemap.morphlelabs.com
    const PRODUCTION_MEDIA_URL = 'https://onemap.morphlelabs.com';

    const getMediaUrl = (url) => {
        if (!url) return url;
        if (url.startsWith('/')) {
            return `${PRODUCTION_MEDIA_URL}${url}`;
        }
        return url;
    };

    // Helper function to extract file name from URL
    const getFileName = (url) => {
        if (!url) return 'File';
        return url.split('/').pop()?.split('?')[0] || 'File';
    };

    // Helper function to get file extension
    const getFileExtension = (url) => {
        const fileName = getFileName(url);
        return fileName.split('.').pop()?.toUpperCase() || '';
    };

    // Helper function to get file type icon color and label
    const getFileTypeInfo = (url) => {
        const ext = getFileExtension(url).toLowerCase();
        if (ext === 'pdf') {
            return { color: 'bg-red-500', text: 'PDF' };
        } else if (['doc', 'docx'].includes(ext)) {
            return { color: 'bg-blue-600', text: 'DOC' };
        } else if (['xls', 'xlsx'].includes(ext)) {
            return { color: 'bg-green-600', text: 'XLS' };
        } else if (['ppt', 'pptx'].includes(ext)) {
            return { color: 'bg-orange-500', text: 'PPT' };
        } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
            return { color: 'bg-yellow-600', text: 'ZIP' };
        } else if (['txt', 'md', 'json', 'xml', 'csv'].includes(ext)) {
            return { color: 'bg-gray-500', text: ext.toUpperCase() };
        }
        return { color: 'bg-gray-500', text: ext.toUpperCase() || 'FILE' };
    };

    // Helper function to download files properly using fetch and blob
    const handleDownload = async (url, filename) => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Fetch failed');
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download via fetch failed, trying direct link:', error);
            // Fallback: open in new tab for download
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'download';
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const mediaPreview = (media_file, media_type) => {
        let preview = null;
        if (media_file === null || media_type === null) {
            return preview;
        }

        const mediaUrl = getMediaUrl(media_file);

        if (media_type === 'image') {
            preview = (
                <div className="max-h-[200px] max-w-[450px] flex justify-center items-center overflow-hidden rounded-lg border bg-muted/30">
                    <img
                        src={mediaUrl}
                        alt="Uploaded Image"
                        className="h-auto w-auto object-contain max-h-[200px] max-w-[450px]"
                    />
                </div>
            );
        } else if (media_type === 'video') {
            preview = (
                <video
                    controls
                    src={mediaUrl}
                    title="Uploaded Video"
                    className="h-[200px] rounded-lg"
                />
            );
        } else if (media_type === 'audio') {
            preview = <ReactAudioPlayer src={mediaUrl} controls />;
        } else {
            // File card for PDFs, DOCs, and other file types
            const fileInfo = getFileTypeInfo(media_file);
            const fileName = getFileName(media_file);
            preview = (
                <div
                    className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors cursor-pointer group max-w-[400px]"
                    onClick={() => handleDownload(mediaUrl, fileName)}
                >
                    <div className={cn("w-10 h-10 rounded flex items-center justify-center shrink-0", fileInfo.color)}>
                        <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                            {fileName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {fileInfo.text} • Click to download
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(mediaUrl, fileName);
                        }}
                    >
                        <Download className="h-4 w-4" />
                    </Button>
                </div>
            );
        }
        return <div className="ml-10 mt-2">{preview}</div>;
    };


    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    return (
        <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">Comments:</h3>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                                "h-8 w-8",
                                filterEnabled ? "text-foreground" : "text-muted-foreground"
                            )}
                            onClick={filterComponent}
                        >
                            <Filter className="h-4 w-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        {filterEnabled ? "Disable filter" : "Enable filter"}
                    </TooltipContent>
                </Tooltip>
            </div>

            <Separator />

            {/* Comments List */}
            <ScrollArea className="h-[400px]">
                <div className="space-y-4 pr-4">
                    {comments.map((comment) => (
                        <div key={comment.id} className="space-y-2">
                            {/* Comment Header */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Avatar className="h-7 w-7">
                                        <AvatarImage src={comment.user.picture} alt={`${comment.user.first_name}'s avatar`} />
                                        <AvatarFallback className="text-xs">
                                            {getInitials(comment.user.first_name, comment.user.last_name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm font-medium text-foreground">
                                        {comment.user.first_name} {comment.user.last_name}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                        {moment(comment.created_at).fromNow()}
                                    </span>
                                </div>

                            </div>

                            {/* Comment Content */}
                            <div className="pl-9">
                                <LinkifyText className="text-sm text-foreground break-words">
                                    {comment.comment}
                                </LinkifyText>
                                {mediaPreview(comment.media_url, comment.media_type)}
                            </div>
                        </div>
                    ))}

                    {/* Empty State */}
                    {comments.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                            No Comments
                        </p>
                    )}
                </div>
            </ScrollArea>

        </div>
    );
};

export default Comments;
